import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Button from './Button';
import { colToday, colMonthBounds } from '../../utils/colTimeUtils';
import './DateRangeFilter.css';

/* Filtro de fechas ÚNICO de la app (F2, brief §7). Presets en hora Colombia
   (colTimeUtils) + rango personalizado con CALENDARIO tipo aerolínea (pedido
   de Miguel 2026-07-22): un popover FLOTANTE (no empuja el layout) donde se
   marca inicio y fin de corrido; un solo día = doble clic en la misma fecha,
   o un clic y "Aplicar" (fin = inicio). Toda la matemática es de calendario
   sobre strings 'YYYY-MM-DD' con Date.UTC — nunca la zona del navegador.
   value = { preset, from, to } (from/to siempre poblados). */

export const DATE_PRESETS = [
  ['today', 'Hoy'],
  ['7d', 'Últimos 7 días'],
  ['14d', 'Últimas 2 semanas'],
  ['month', 'Este mes'],
  ['prevMonth', 'Mes anterior'],
  ['custom', 'Personalizado'],
];

const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
const addDays = (s, n) => {
  const [y, m, d] = s.split('-').map(Number);
  return ymd(new Date(Date.UTC(y, m - 1, d + n)));
};
const addMonths = (ym, n) => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`;
};

export function resolvePreset(preset, customFrom, customTo) {
  const today = colToday();
  if (preset === 'all') return { from: null, to: null }; // sin acotar (historiales)
  if (preset === 'today') return { from: today, to: today };
  if (preset === '7d') return { from: addDays(today, -6), to: today };
  if (preset === '14d') return { from: addDays(today, -13), to: today };
  if (preset === 'prevMonth') {
    const b = colMonthBounds(-1);
    return { from: b.first, to: b.last };
  }
  if (preset === 'custom') return { from: customFrom || today, to: customTo || today };
  return { from: colMonthBounds(0).first, to: today }; // 'month'
}

const MES_LABEL = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('es-CO', { month: 'long', year: 'numeric', timeZone: 'UTC' });
};

const fmtCorto = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
};

/* Etiqueta COMPACTA del chip (corrección Miguel 2026-07-22, regla del fold):
   "15–22 jul" · "15 jul – 3 ago" · año solo si no es el actual. */
const mesCorto = (m) => new Date(Date.UTC(2000, m - 1, 1))
  .toLocaleDateString('es-CO', { month: 'short', timeZone: 'UTC' }).replace('.', '');

function fmtChip(from, to) {
  const [y1, m1, d1] = from.split('-').map(Number);
  const [y2, m2, d2] = to.split('-').map(Number);
  const añoActual = Number(colToday().slice(0, 4));
  const sufijo = (y) => (y === añoActual ? '' : ` ${y}`);
  if (from === to) return `${d1} ${mesCorto(m1)}${sufijo(y1)}`;
  if (y1 === y2 && m1 === m2) return `${d1}–${d2} ${mesCorto(m1)}${sufijo(y1)}`;
  if (y1 === y2) return `${d1} ${mesCorto(m1)} – ${d2} ${mesCorto(m2)}${sufijo(y1)}`;
  return `${d1} ${mesCorto(m1)} ${y1} – ${d2} ${mesCorto(m2)} ${y2}`;
}

/* Un mes del calendario. Semana inicia LUNES (convención es-CO). */
function MesGrid({ ym, draft, hover, onPick, onHover, hoy, className = '' }) {
  const [y, m] = ym.split('-').map(Number);
  const ultimoDia = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const lunesIndex = (new Date(Date.UTC(y, m - 1, 1)).getUTCDay() + 6) % 7;

  const { start, end } = draft;
  const previewEnd = end || (hover && start && hover > start ? hover : null);
  const enRango = (s) => start && previewEnd && s > start && s < previewEnd;

  const celdas = [];
  for (let i = 0; i < lunesIndex; i++) celdas.push(null);
  for (let d = 1; d <= ultimoDia; d++) celdas.push(`${ym}-${pad(d)}`);

  return (
    <div className={`ui-cal-mes${className ? ` ${className}` : ''}`}>
      <div className="ui-cal-mes-titulo">{MES_LABEL(ym)}</div>
      <div className="ui-cal-grid">
        {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((d, i) => (
          <span key={`h${i}`} className="ui-cal-diahead" aria-hidden="true">{d}</span>
        ))}
        {celdas.map((s, i) => s === null
          ? <span key={`v${i}`} />
          : (
            <button
              key={s}
              type="button"
              className={[
                'ui-cal-dia',
                s === start ? 'is-start' : '',
                s === end ? 'is-end' : '',
                enRango(s) ? 'is-in-range' : '',
                s === hoy ? 'is-today' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => onPick(s)}
              onMouseEnter={() => onHover(s)}
            >{Number(s.slice(8))}</button>
          ))}
      </div>
    </div>
  );
}

/* allowAll (KIT CRECE F4): agrega el preset "Todo el historial" ({from:null,
   to:null}) para listas que hoy arrancan SIN filtro de fechas (retiros de
   Inventario) — la regla global de fechas no puede recortarles el historial
   por defecto. El caller que lo usa arranca con preset 'all'. */
export default function DateRangeFilter({ value, onChange, allowAll = false, className = '' }) {
  const preset = value?.preset || (allowAll ? 'all' : 'month');
  const presets = allowAll ? [['all', 'Todo el historial'], ...DATE_PRESETS] : DATE_PRESETS;
  const presetBase = allowAll ? 'all' : 'month'; // a dónde vuelve el × del rango custom
  const wrapRef = useRef(null);
  const popRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [draft, setDraft] = useState({ start: null, end: null });
  const [hover, setHover] = useState(null);
  const [baseYm, setBaseYm] = useState(() => colToday().slice(0, 7));
  const hoy = colToday();

  const abrir = () => {
    const r = wrapRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 6, left: r.left });
    const from = preset === 'custom' ? value?.from : null;
    const to = preset === 'custom' ? value?.to : null;
    setDraft({ start: from, end: to });
    setBaseYm((from || hoy).slice(0, 7));
    setHover(null);
    setOpen(true);
  };

  /* Flip/reposición contra los bordes (corrección Miguel 2026-07-22, mismo
     criterio que OverflowMenu): tras el primer paint se mide el popover REAL —
     si se sale por la derecha se corre; si no cabe abajo, abre hacia arriba. */
  useEffect(() => {
    if (!open || !popRef.current || !wrapRef.current) return;
    const p = popRef.current.getBoundingClientRect();
    const a = wrapRef.current.getBoundingClientRect();
    let top = a.bottom + 6;
    let left = a.left;
    if (left + p.width > window.innerWidth - 8) left = Math.max(8, window.innerWidth - 8 - p.width);
    if (top + p.height > window.innerHeight - 8) top = Math.max(8, a.top - p.height - 6);
    setPos({ top, left });
  }, [open]);

  const cerrar = () => setOpen(false); // sin aplicar: el value comprometido no cambió

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.type === 'keydown') { if (e.key === 'Escape') cerrar(); return; }
      if (popRef.current?.contains(e.target) || wrapRef.current?.contains(e.target)) return;
      cerrar();
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', handler);
    };
  }, [open]);

  const pick = (s) => {
    setDraft((d) => {
      if (!d.start || (d.start && d.end)) return { start: s, end: null };
      if (s < d.start) return { start: s, end: null };  // clic antes del inicio: reinicia
      return { start: d.start, end: s };                 // incluye s === start (mismo día)
    });
  };

  const aplicar = () => {
    if (!draft.start) return;
    const from = draft.start;
    const to = draft.end || draft.start; // un solo día: fin = inicio
    onChange?.({ preset: 'custom', from, to });
    setOpen(false);
  };

  const cambiarPreset = (p) => {
    if (p === 'custom') { abrir(); return; } // no compromete hasta Aplicar
    setOpen(false);
    onChange?.({ preset: p, ...resolvePreset(p) });
  };

  return (
    <div ref={wrapRef} className={`ui-daterange${className ? ` ${className}` : ''}`}>
      <select value={preset} onChange={(e) => cambiarPreset(e.target.value)} aria-label="Período">
        {presets.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
      </select>
      {preset === 'custom' && (
        <span className="ui-daterange-display">
          <button type="button" className="ui-daterange-display-label" onClick={abrir} title="Cambiar el rango">
            {fmtChip(value.from, value.to)}
          </button>
          <button
            type="button"
            className="ui-daterange-display-clear"
            aria-label="Quitar el rango personalizado"
            title={allowAll ? 'Quitar (vuelve a Todo el historial)' : 'Quitar (vuelve a Este mes)'}
            onClick={() => onChange?.({ preset: presetBase, ...resolvePreset(presetBase) })}
          >×</button>
        </span>
      )}

      {open && pos && createPortal(
        <div ref={popRef} className="ui-cal-popover" style={{ top: pos.top, left: pos.left }} role="dialog" aria-label="Elegir rango de fechas">
          <div className="ui-cal-meses" onMouseLeave={() => setHover(null)}>
            <button type="button" className="ui-cal-nav ui-cal-nav--prev" onClick={() => setBaseYm(addMonths(baseYm, -1))} aria-label="Mes anterior">‹</button>
            <button type="button" className="ui-cal-nav ui-cal-nav--next" onClick={() => setBaseYm(addMonths(baseYm, 1))} aria-label="Mes siguiente">›</button>
            <MesGrid ym={baseYm} draft={draft} hover={hover} onPick={pick} onHover={setHover} hoy={hoy} />
            <MesGrid ym={addMonths(baseYm, 1)} draft={draft} hover={hover} onPick={pick} onHover={setHover} hoy={hoy} className="ui-cal-mes--segundo" />
          </div>
          <div className="ui-cal-footer">
            <span className="ui-cal-hint">
              {!draft.start && 'Elige el día de inicio'}
              {draft.start && !draft.end && `Inicio: ${fmtCorto(draft.start)} — elige el fin (o Aplicar para un solo día)`}
              {draft.start && draft.end && (draft.start === draft.end ? fmtCorto(draft.start) : `${fmtCorto(draft.start)} – ${fmtCorto(draft.end)}`)}
            </span>
            <Button variant="tertiary" size="sm" onClick={cerrar}>Cancelar</Button>
            <Button variant="primary" size="sm" disabled={!draft.start} onClick={aplicar}>Aplicar</Button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
